import agentWebSocket from "@app/config/agentWebSocket";
import { queryClient } from "@app/config/api/api";
import { agentSessionMessagesOptions, translateBackendMessages, useAgentSessionMessages } from "@app/queries/agent";
import { useExperimentStatus } from "@app/state/store";
import { useAuth0 } from "@auth0/auth0-react";
import type { AgentAttachmentPayload, AgentServerMessage, ExperimentId } from "@globals";
import type { ChatAttachment, ChatMessage } from "@lib/agent/types";
import { notifications } from "@lib/notification/notification";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

let messageIdCounter = 0;
function nextId(): string {
  return `msg-${++messageIdCounter}`;
}

async function fileToBase64(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  // Encode in chunks to avoid hitting argument length limits on String.fromCharCode
  // for large files (4.5 MB cap → ~4.5M args would blow the stack).
  let binary = "";
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

export function useAgentChat(experimentId: ExperimentId, teamId: string | null) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const streamBufferRef = useRef("");
  const currentAssistantIdRef = useRef<string | null>(null);
  const toolCallsRef = useRef<string[]>([]);
  // Seeding the local message list happens once. Subsequent refetches (e.g. window-focus)
  // must not clobber turns that have streamed in since.
  const hasSeededRef = useRef(false);
  const experimentIdRef = useRef(experimentId);
  useEffect(() => {
    experimentIdRef.current = experimentId;
  }, [experimentId]);

  const { getAccessTokenSilently } = useAuth0();

  const {
    data: historyData,
    isLoading: isHistoryLoading,
    isError: historyError,
    refetch: refetchHistory
  } = useAgentSessionMessages(experimentId);

  // Seed local state from persisted history exactly once.
  //
  // Skip the setMessages call when history is empty. Effect order is child-first within a
  // commit, so on a fresh draft entered from `?query=...` ExperimentAgentChat's handoff
  // effect dispatches `send()` (which appends the user + pending-assistant messages to
  // local state) BEFORE this parent effect fires. A blanket `setMessages([...])` would
  // then overwrite that local state with the empty server history and the typed message
  // would vanish. Empty history has nothing to restore anyway.
  useEffect(() => {
    if (hasSeededRef.current || !historyData) {
      return;
    }
    hasSeededRef.current = true;
    if (historyData.messages.length === 0) {
      return;
    }
    setMessages(translateBackendMessages(historyData.messages));
  }, [historyData]);

  const clearLocal = useCallback(() => {
    setMessages([]);
    streamBufferRef.current = "";
    currentAssistantIdRef.current = null;
    toolCallsRef.current = [];
    setIsStreaming(false);
  }, []);

  // Re-open the seed gate so the next `historyData` change rehydrates messages from the
  // server. Used by the optimistic-clear rollback path: after `clearLocal()` empties the
  // UI and the DELETE fails, the consumer calls this + `refetchHistory()` to recover.
  // We don't reset the gate inside `clearLocal` itself because on the success path that
  // would race a user typing a new message against the post-invalidate refetch.
  const allowReseed = useCallback(() => {
    hasSeededRef.current = false;
  }, []);

  // When the experiment status changes (e.g. draft → created via the publish flow), the
  // agent consumer has injected a `[SYSTEM] …` row into the persisted session. The agent
  // WS push that announces it (`agent.session.message_appended`) is best-effort and gets
  // lost if the chat is mid-unmount during navigation. The notifications WS — which
  // updates the global `experimentStatus` map — is independent of the chat's lifecycle,
  // so we reuse it as a "something changed on the server, resync" signal.
  //
  // Two guards:
  //   - Only act on real status transitions (compare against a ref; ignore the initial
  //     status push that just primes the cache).
  //   - Skip while a stream is in flight — `setMessages(translateBackendMessages(...))`
  //     would otherwise wipe the assistant bubble currently accumulating chunks.
  const liveStatus = useExperimentStatus(experimentId);
  const lastSeenStatusRef = useRef<typeof liveStatus>(undefined);
  useEffect(() => {
    const previous = lastSeenStatusRef.current;
    lastSeenStatusRef.current = liveStatus;
    if (previous === undefined || previous === liveStatus || liveStatus === undefined) {
      return;
    }
    if (isStreaming) {
      return;
    }
    hasSeededRef.current = false;
    void queryClient.invalidateQueries({
      queryKey: agentSessionMessagesOptions(experimentId).queryKey
    });
  }, [liveStatus, experimentId, isStreaming]);

  // Connect on mount, reconnect when the experiment's team changes
  useEffect(() => {
    void agentWebSocket.connect(() => getAccessTokenSilently(), teamId);
    return () => agentWebSocket.close();
  }, [teamId, getAccessTokenSilently]);

  // Track connection state
  useEffect(() => agentWebSocket.onStateChange(state => setIsReady(state === "connected")), []);

  // Handle server messages
  useEffect(() => {
    const handleMessage = (message: AgentServerMessage) => {
      switch (message.action) {
        case "agent.response.chunk": {
          streamBufferRef.current += message.payload.message;
          const buffered = streamBufferRef.current;
          const assistantId = currentAssistantIdRef.current;
          if (assistantId) {
            setMessages(prev => prev.map(m => (m.id === assistantId ? { ...m, content: buffered } : m)));
          }
          break;
        }
        case "agent.tool_use": {
          toolCallsRef.current = [...toolCallsRef.current, message.payload.tool];
          const tools = toolCallsRef.current;
          // Insert a paragraph break so text that resumes after the tool call
          // renders as a separate markdown paragraph from the pre-tool text.
          if (streamBufferRef.current && !streamBufferRef.current.endsWith("\n\n")) {
            streamBufferRef.current += "\n\n";
          }
          const buffered = streamBufferRef.current;
          const assistantId = currentAssistantIdRef.current;
          if (assistantId) {
            setMessages(prev =>
              prev.map(m => (m.id === assistantId ? { ...m, content: buffered, toolCalls: tools } : m))
            );
          }
          break;
        }
        case "agent.response.done": {
          if (message.payload?.extras) {
            const extras = message.payload.extras;
            const assistantId = currentAssistantIdRef.current;
            if (assistantId) {
              setMessages(prev => prev.map(m => (m.id === assistantId ? { ...m, extras } : m)));
            }
          }
          setIsStreaming(false);
          currentAssistantIdRef.current = null;
          streamBufferRef.current = "";
          toolCallsRef.current = [];
          // The agent service has now persisted the AgentSession row and committed messages —
          // refresh so `agent_session_id` surfaces and the committed message count signal updates.
          void queryClient.invalidateQueries({
            queryKey: agentSessionMessagesOptions(experimentIdRef.current).queryKey
          });
          break;
        }
        case "agent.session.message_appended": {
          // External event injected a message into the persisted session (e.g. an
          // experiment status transition). The DB write is authoritative; this WS push is
          // just the live cue so we append immediately instead of waiting on a refetch.
          setMessages(prev => [
            ...prev,
            { id: nextId(), role: message.payload.role, content: message.payload.content }
          ]);
          break;
        }
        case "agent.error": {
          const assistantId = currentAssistantIdRef.current;
          if (assistantId) {
            setMessages(prev =>
              prev.map(m => (m.id === assistantId ? { ...m, content: m.content || message.payload.message } : m))
            );
          } else {
            setMessages(prev => [...prev, { id: nextId(), role: "assistant", content: message.payload.message }]);
          }
          setIsStreaming(false);
          currentAssistantIdRef.current = null;
          streamBufferRef.current = "";
          toolCallsRef.current = [];
          break;
        }
      }
    };

    return agentWebSocket.onServerMessage(handleMessage);
  }, []);

  const send = useCallback(
    (content: string, files: File[] = []): void => {
      if (!content.trim() || isStreaming || isHistoryLoading || historyError) {
        return;
      }

      const messageAttachments: ChatAttachment[] | undefined =
        files.length > 0 ? files.map(f => ({ filename: f.name })) : undefined;

      const userMessage: ChatMessage = {
        id: nextId(),
        role: "user",
        content,
        attachments: messageAttachments
      };
      const assistantMessage: ChatMessage = { id: nextId(), role: "assistant", content: "" };
      currentAssistantIdRef.current = assistantMessage.id;
      streamBufferRef.current = "";
      toolCallsRef.current = [];

      setMessages(prev => [...prev, userMessage, assistantMessage]);
      setIsStreaming(true);

      // Encode files asynchronously, then dispatch. Encoding a 4.5 MB file via
      // FileReader is fast (tens of ms), so the perceived latency between user
      // submit and the WS send is negligible. If encoding fails (e.g. an unreadable
      // File handle), unstick the UI rather than leaving a forever-streaming bubble.
      const assistantId = assistantMessage.id;
      void (async () => {
        try {
          const attachmentPayloads: AgentAttachmentPayload[] = await Promise.all(
            files.map(async file => ({ filename: file.name, content_b64: await fileToBase64(file) }))
          );
          agentWebSocket.send({
            action: "agent.prompt",
            payload: {
              message: content,
              experiment_id: experimentId,
              ...(attachmentPayloads.length > 0 && { attachments: attachmentPayloads })
            }
          });
        } catch {
          notifications.error("Failed to attach files", "Could not read one or more attachments. Please try again.");
          setMessages(prev => prev.filter(m => m.id !== assistantId));
          currentAssistantIdRef.current = null;
          streamBufferRef.current = "";
          toolCallsRef.current = [];
          setIsStreaming(false);
        }
      })();
    },
    [isStreaming, isHistoryLoading, historyError, experimentId]
  );

  // Cumulative attached-doc count across the user's turns in this session.
  // Used by the input to enforce the same MAX_DOCS_PER_SESSION cap as the backend
  // before submit, so the user gets immediate feedback instead of an agent error.
  const sessionAttachmentCount = useMemo(
    () => messages.reduce((sum, m) => sum + (m.role === "user" ? (m.attachments?.length ?? 0) : 0), 0),
    [messages]
  );

  const agentSessionId = historyData?.agent_session_id ?? null;

  return {
    messages,
    isStreaming,
    isReady,
    send,
    sessionAttachmentCount,
    isHistoryLoading,
    historyError,
    refetchHistory,
    clearLocal,
    allowReseed,
    // Derived from `historyData` (not from local `messages`) so callers gating on "is
    // there persisted history?" can rely on it during the same render the seed effect
    // commits — the local `messages` state lags by one render due to setState batching.
    hasRestoredHistory: Boolean(historyData?.messages?.length),
    agentSessionId,
    committedMessageCount: historyData?.messages?.length ?? 0
  };
}
